import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { toast } from "sonner";
import { FileText, CheckCircle2, Send, Trash2, MessageCircle, Loader2 } from "lucide-react";

const eur = (n) => `${Number(n).toFixed(2).replace(".", ",")} €`;
const ZONE = { napoli: "Napoli e Provincia", italia: "Resto d'Italia", ritiro: "Ritiro di persona" };
const STATI = {
  in_attesa: { label: "In attesa", cls: "bg-amber-400/15 text-amber-300 border-amber-400/30" },
  approvato: { label: "Approvato", cls: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30" },
  trasferito: { label: "Trasferito", cls: "bg-cyan-400/15 text-cyan-300 border-cyan-400/30" },
};

export default function Preventivi() {
  const navigate = useNavigate();
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("");

  const load = () => {
    setLoading(true);
    api.get(`/preventivi${filtro ? `?stato=${filtro}` : ""}`).then(({ data }) => setLista(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [filtro]); // eslint-disable-line

  const buildQuoteText = (p) => {
    const isRitiro = p.zona === "ritiro";
    const lines = (p.righe || []).map((r) => `• ${r.quantita}× ${r.tipo_capo} ${r.colore} ${r.taglia}${r.grafica ? " · " + r.grafica : ""}${r.stampe_plus ? ` · ${r.stampe_plus} Stampe Plus` : ""}: ${eur(r.prezzo_unitario * r.quantita)}`);
    const sped = p.spedizione === 0 ? (isRitiro ? "Ritiro di persona (gratis)" : "Spedizione gratuita") : `Spedizione: ${eur(p.spedizione)}`;
    return `Ecco a te il tuo preventivo Gigli Edition:\n\n${lines.join("\n")}\n\n${sped}\nTotale: ${eur(p.totale)}\n\nFammi sapere se confermi e procediamo! 😊`;
  };

  const reinviaWhatsapp = (p) => {
    const tel = (p.whatsapp || "").replace(/\D/g, "");
    if (tel.length < 8) return toast.error("Numero WhatsApp non presente in questo preventivo.");
    window.open(`https://wa.me/${tel}?text=${encodeURIComponent(buildQuoteText(p))}`, "_blank");
  };

  const segnaApprovato = async (p) => {
    try { await api.patch(`/preventivi/${p.id}/stato`, { stato: "approvato" }); toast.success("Preventivo approvato."); load(); }
    catch { toast.error("Errore aggiornamento stato."); }
  };

  const trasferisci = (p) => navigate("/vendita-rapida", { state: { preventivo: p } });

  const elimina = async (p) => {
    try { await api.delete(`/preventivi/${p.id}`); toast.success("Preventivo eliminato."); load(); }
    catch { toast.error("Errore eliminazione."); }
  };

  return (
    <Layout>
      <div className="mb-6 flex items-center gap-2.5">
        <div className="w-10 h-10 rounded-xl bg-amber-400/15 border border-amber-400/30 flex items-center justify-center">
          <FileText className="w-5 h-5 text-amber-300" />
        </div>
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-100 tracking-tight">Preventivi</h1>
          <p className="text-slate-500 text-sm">Gestisci i preventivi inviati e trasferiscili in Vendita Rapida dopo l'approvazione</p>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {[["", "Tutti"], ["in_attesa", "In attesa"], ["approvato", "Approvati"], ["trasferito", "Trasferiti"]].map(([v, l]) => (
          <button key={v} data-testid={`filtro-${v || "tutti"}`} onClick={() => setFiltro(v)} className={`px-3 py-1.5 rounded-lg text-sm border ${filtro === v ? "bg-cyan-400/15 text-cyan-300 border-cyan-400/40" : "text-slate-400 border-slate-700 hover:bg-slate-800"}`}>{l}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /> Caricamento...</div>
      ) : lista.length === 0 ? (
        <p data-testid="preventivi-empty" className="text-slate-500 text-sm">Nessun preventivo trovato.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="preventivi-list">
          {lista.map((p) => {
            const st = STATI[p.stato] || STATI.in_attesa;
            return (
              <div key={p.id} data-testid={`preventivo-${p.id}`} className="glass rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono-tech text-slate-300 text-sm">{p.numero}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
                </div>
                <div className="space-y-1">
                  {(p.righe || []).map((r, i) => (
                    <div key={i} className="text-sm text-slate-300 flex justify-between gap-2">
                      <span className="truncate">{r.quantita}× {r.tipo_capo} {r.colore} {r.taglia}</span>
                      <span className="font-mono-tech text-slate-400">{eur(r.prezzo_unitario * r.quantita)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between text-sm border-t border-slate-800/60 pt-2">
                  <span className="text-slate-500">{ZONE[p.zona]} {p.whatsapp ? `· ${p.whatsapp}` : ""}</span>
                  <span className="font-mono-tech text-emerald-300 font-bold">{eur(p.totale)}</span>
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  {p.stato === "in_attesa" && (
                    <button data-testid={`approva-${p.id}`} onClick={() => segnaApprovato(p)} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25"><CheckCircle2 className="w-3.5 h-3.5" /> Approva</button>
                  )}
                  <button data-testid={`trasferisci-${p.id}`} onClick={() => trasferisci(p)} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/25"><Send className="w-3.5 h-3.5" /> Trasferisci in Vendita Rapida</button>
                  {p.whatsapp && (
                    <button data-testid={`reinvia-${p.id}`} onClick={() => reinviaWhatsapp(p)} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-[#25D366]/15 text-[#25D366] border border-[#25D366]/30 hover:bg-[#25D366]/25"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</button>
                  )}
                  <button data-testid={`elimina-${p.id}`} onClick={() => elimina(p)} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-red-500/10 text-red-300 border border-red-500/30 hover:bg-red-500/20"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Layout>
  );
}
